/**
 * proef's wine-region lexicon: a stable code per region, so a card can say "Piedmont" without a
 * typed string. Regions are broad on purpose (a card counts places, not parcels); the best-known
 * appellations are aliases of the region they sit in, so a bottle labelled "Pauillac" still counts
 * for Bordeaux. Names are place names and are not translated. Our own list.
 */
import { foldName } from "./fold.js";

export interface Region {
  /** `<country lower-case>-<kebab name>`, ASCII, stable across revisions. */
  readonly code: string;
  /** ISO 3166-1 alpha-2, a code the country lexicon holds. */
  readonly country: string;
  readonly name: string;
  readonly aliases?: readonly string[];
}

export const REGIONS: readonly Region[] = [
  // ---- France ----
  {
    code: "fr-bordeaux",
    country: "FR",
    name: "Bordeaux",
    aliases: [
      "Médoc",
      "Haut-Médoc",
      "Pauillac",
      "Margaux",
      "Saint-Julien",
      "Saint-Estèphe",
      "Saint-Émilion",
      "Pomerol",
      "Pessac-Léognan",
      "Graves",
      "Sauternes",
      "Entre-Deux-Mers",
    ],
  },
  {
    code: "fr-burgundy",
    country: "FR",
    name: "Burgundy",
    aliases: [
      "Bourgogne",
      "Chablis",
      "Côte d'Or",
      "Côte de Nuits",
      "Côte de Beaune",
      "Gevrey-Chambertin",
      "Vosne-Romanée",
      "Nuits-Saint-Georges",
      "Meursault",
      "Puligny-Montrachet",
      "Chassagne-Montrachet",
      "Pommard",
      "Volnay",
      "Mâconnais",
      "Pouilly-Fuissé",
    ],
  },
  {
    code: "fr-beaujolais",
    country: "FR",
    name: "Beaujolais",
    aliases: ["Morgon", "Fleurie", "Brouilly", "Moulin-à-Vent", "Juliénas"],
  },
  { code: "fr-champagne", country: "FR", name: "Champagne" },
  {
    code: "fr-loire",
    country: "FR",
    name: "Loire",
    aliases: [
      "Loire Valley",
      "Vallée de la Loire",
      "Sancerre",
      "Pouilly-Fumé",
      "Vouvray",
      "Muscadet",
      "Anjou",
      "Saumur",
      "Chinon",
      "Touraine",
      "Savennières",
    ],
  },
  {
    code: "fr-rhone",
    country: "FR",
    name: "Rhône",
    aliases: [
      "Rhone Valley",
      "Vallée du Rhône",
      "Côtes du Rhône",
      "Châteauneuf-du-Pape",
      "Hermitage",
      "Crozes-Hermitage",
      "Côte-Rôtie",
      "Condrieu",
      "Gigondas",
      "Cornas",
    ],
  },
  { code: "fr-alsace", country: "FR", name: "Alsace" },
  {
    code: "fr-languedoc",
    country: "FR",
    name: "Languedoc",
    aliases: [
      "Pays d'Oc",
      "Corbières",
      "Minervois",
      "Pic Saint-Loup",
      "Faugères",
      "Picpoul de Pinet",
    ],
  },
  {
    code: "fr-roussillon",
    country: "FR",
    name: "Roussillon",
    aliases: ["Banyuls", "Collioure", "Maury", "Rivesaltes"],
  },
  {
    code: "fr-provence",
    country: "FR",
    name: "Provence",
    aliases: ["Côtes de Provence", "Bandol", "Cassis"],
  },
  { code: "fr-jura", country: "FR", name: "Jura", aliases: ["Arbois", "Château-Chalon"] },
  { code: "fr-savoie", country: "FR", name: "Savoie", aliases: ["Savoy"] },
  {
    code: "fr-south-west",
    country: "FR",
    name: "South West France",
    aliases: ["Sud-Ouest", "Cahors", "Madiran", "Jurançon", "Gaillac", "Bergerac", "Monbazillac"],
  },
  { code: "fr-corsica", country: "FR", name: "Corsica", aliases: ["Corse"] },
  // ---- Italy ----
  {
    code: "it-piedmont",
    country: "IT",
    name: "Piedmont",
    aliases: ["Piemonte", "Barolo", "Barbaresco", "Langhe", "Roero", "Asti", "Gavi", "Gattinara"],
  },
  {
    code: "it-tuscany",
    country: "IT",
    name: "Tuscany",
    aliases: [
      "Toscana",
      "Chianti",
      "Chianti Classico",
      "Brunello di Montalcino",
      "Montalcino",
      "Bolgheri",
      "Vino Nobile di Montepulciano",
    ],
  },
  {
    code: "it-veneto",
    country: "IT",
    name: "Veneto",
    aliases: ["Valpolicella", "Amarone", "Soave", "Prosecco", "Valdobbiadene", "Bardolino"],
  },
  {
    code: "it-lombardy",
    country: "IT",
    name: "Lombardy",
    aliases: ["Lombardia", "Franciacorta", "Valtellina", "Oltrepò Pavese"],
  },
  {
    code: "it-friuli",
    country: "IT",
    name: "Friuli-Venezia Giulia",
    aliases: ["Friuli", "Collio", "Colli Orientali del Friuli"],
  },
  {
    code: "it-trentino-alto-adige",
    country: "IT",
    name: "Trentino-Alto Adige",
    aliases: ["Alto Adige", "Südtirol", "Trentino"],
  },
  { code: "it-emilia-romagna", country: "IT", name: "Emilia-Romagna", aliases: ["Romagna"] },
  { code: "it-abruzzo", country: "IT", name: "Abruzzo", aliases: ["Abruzzi"] },
  { code: "it-marche", country: "IT", name: "Marche", aliases: ["The Marches", "Conero"] },
  { code: "it-umbria", country: "IT", name: "Umbria", aliases: ["Orvieto", "Montefalco"] },
  { code: "it-lazio", country: "IT", name: "Lazio", aliases: ["Latium"] },
  { code: "it-campania", country: "IT", name: "Campania", aliases: ["Taurasi", "Irpinia"] },
  { code: "it-puglia", country: "IT", name: "Puglia", aliases: ["Apulia", "Salento"] },
  { code: "it-sicily", country: "IT", name: "Sicily", aliases: ["Sicilia", "Etna"] },
  { code: "it-sardinia", country: "IT", name: "Sardinia", aliases: ["Sardegna"] },
  { code: "it-basilicata", country: "IT", name: "Basilicata", aliases: ["Aglianico del Vulture"] },
  { code: "it-calabria", country: "IT", name: "Calabria" },
  { code: "it-liguria", country: "IT", name: "Liguria", aliases: ["Cinque Terre"] },
  // ---- Spain ----
  {
    code: "es-rioja",
    country: "ES",
    name: "Rioja",
    aliases: ["La Rioja", "Rioja Alta", "Rioja Alavesa", "Rioja Baja"],
  },
  { code: "es-ribera-del-duero", country: "ES", name: "Ribera del Duero" },
  { code: "es-priorat", country: "ES", name: "Priorat", aliases: ["Priorato"] },
  { code: "es-montsant", country: "ES", name: "Montsant" },
  { code: "es-rias-baixas", country: "ES", name: "Rías Baixas", aliases: ["Rias Baixas"] },
  { code: "es-ribeira-sacra", country: "ES", name: "Ribeira Sacra" },
  {
    code: "es-jerez",
    country: "ES",
    name: "Jerez",
    aliases: ["Sherry", "Jerez-Xérès", "Manzanilla", "Sanlúcar de Barrameda"],
  },
  { code: "es-penedes", country: "ES", name: "Penedès", aliases: ["Penedes", "Cava"] },
  { code: "es-navarra", country: "ES", name: "Navarra" },
  { code: "es-rueda", country: "ES", name: "Rueda" },
  { code: "es-toro", country: "ES", name: "Toro" },
  { code: "es-bierzo", country: "ES", name: "Bierzo" },
  { code: "es-la-mancha", country: "ES", name: "La Mancha" },
  { code: "es-jumilla", country: "ES", name: "Jumilla" },
  { code: "es-valencia", country: "ES", name: "Valencia", aliases: ["Utiel-Requena"] },
  {
    code: "es-canary-islands",
    country: "ES",
    name: "Canary Islands",
    aliases: ["Islas Canarias", "Tenerife"],
  },
  // ---- Portugal ----
  { code: "pt-douro", country: "PT", name: "Douro", aliases: ["Duoro", "Porto", "Port"] },
  { code: "pt-alentejo", country: "PT", name: "Alentejo" },
  { code: "pt-vinho-verde", country: "PT", name: "Vinho Verde" },
  { code: "pt-dao", country: "PT", name: "Dão", aliases: ["Dao"] },
  { code: "pt-bairrada", country: "PT", name: "Bairrada" },
  { code: "pt-madeira", country: "PT", name: "Madeira" },
  { code: "pt-lisboa", country: "PT", name: "Lisboa", aliases: ["Lisbon", "Colares", "Bucelas"] },
  { code: "pt-tejo", country: "PT", name: "Tejo", aliases: ["Ribatejo"] },
  {
    code: "pt-setubal",
    country: "PT",
    name: "Setúbal",
    aliases: ["Península de Setúbal", "Palmela"],
  },
  // ---- Germany ----
  { code: "de-mosel", country: "DE", name: "Mosel", aliases: ["Mosel-Saar-Ruwer", "Moselle"] },
  { code: "de-rheingau", country: "DE", name: "Rheingau" },
  { code: "de-pfalz", country: "DE", name: "Pfalz", aliases: ["Palatinate"] },
  { code: "de-rheinhessen", country: "DE", name: "Rheinhessen" },
  { code: "de-nahe", country: "DE", name: "Nahe" },
  { code: "de-baden", country: "DE", name: "Baden" },
  { code: "de-franken", country: "DE", name: "Franken", aliases: ["Franconia"] },
  { code: "de-wurttemberg", country: "DE", name: "Württemberg" },
  { code: "de-ahr", country: "DE", name: "Ahr" },
  // ---- Austria ----
  { code: "at-wachau", country: "AT", name: "Wachau" },
  { code: "at-kamptal", country: "AT", name: "Kamptal" },
  { code: "at-kremstal", country: "AT", name: "Kremstal" },
  { code: "at-burgenland", country: "AT", name: "Burgenland" },
  { code: "at-styria", country: "AT", name: "Styria", aliases: ["Steiermark"] },
  {
    code: "at-lower-austria",
    country: "AT",
    name: "Lower Austria",
    aliases: ["Niederösterreich", "Weinviertel"],
  },
  { code: "at-vienna", country: "AT", name: "Vienna", aliases: ["Wien"] },
  // ---- the rest of Europe and the Mediterranean ----
  { code: "ch-valais", country: "CH", name: "Valais", aliases: ["Wallis"] },
  { code: "ch-vaud", country: "CH", name: "Vaud", aliases: ["Lavaux"] },
  { code: "ch-geneva", country: "CH", name: "Geneva", aliases: ["Genève"] },
  { code: "hu-tokaj", country: "HU", name: "Tokaj", aliases: ["Tokaji"] },
  { code: "hu-eger", country: "HU", name: "Eger" },
  { code: "gr-santorini", country: "GR", name: "Santorini" },
  { code: "gr-macedonia", country: "GR", name: "Macedonia", aliases: ["Naoussa"] },
  { code: "gr-peloponnese", country: "GR", name: "Peloponnese", aliases: ["Nemea"] },
  { code: "si-primorska", country: "SI", name: "Primorska", aliases: ["Goriška Brda", "Brda"] },
  { code: "hr-istria", country: "HR", name: "Istria", aliases: ["Istra"] },
  { code: "hr-dalmatia", country: "HR", name: "Dalmatia", aliases: ["Dalmacija"] },
  { code: "ro-transylvania", country: "RO", name: "Transylvania" },
  { code: "ge-kakheti", country: "GE", name: "Kakheti" },
  {
    code: "lu-moselle",
    country: "LU",
    name: "Luxembourg Moselle",
    aliases: ["Moselle Luxembourgeoise"],
  },
  { code: "gb-england", country: "GB", name: "England", aliases: ["Sussex", "Kent"] },
  // ---- the Americas ----
  { code: "us-napa-valley", country: "US", name: "Napa Valley", aliases: ["Napa"] },
  {
    code: "us-sonoma",
    country: "US",
    name: "Sonoma",
    aliases: ["Sonoma County", "Russian River Valley"],
  },
  {
    code: "us-central-coast",
    country: "US",
    name: "Central Coast",
    aliases: ["Paso Robles", "Santa Barbara", "Santa Cruz Mountains"],
  },
  { code: "us-willamette-valley", country: "US", name: "Willamette Valley", aliases: ["Oregon"] },
  {
    code: "us-washington",
    country: "US",
    name: "Washington State",
    aliases: ["Columbia Valley", "Walla Walla"],
  },
  { code: "us-finger-lakes", country: "US", name: "Finger Lakes" },
  { code: "ca-niagara", country: "CA", name: "Niagara Peninsula", aliases: ["Niagara"] },
  { code: "ca-okanagan", country: "CA", name: "Okanagan Valley", aliases: ["Okanagan"] },
  { code: "ar-mendoza", country: "AR", name: "Mendoza", aliases: ["Luján de Cuyo", "Uco Valley"] },
  { code: "ar-salta", country: "AR", name: "Salta", aliases: ["Cafayate"] },
  { code: "ar-patagonia", country: "AR", name: "Patagonia", aliases: ["Neuquén", "Río Negro"] },
  { code: "cl-maipo", country: "CL", name: "Maipo Valley", aliases: ["Maipo"] },
  { code: "cl-colchagua", country: "CL", name: "Colchagua Valley", aliases: ["Colchagua"] },
  { code: "cl-casablanca", country: "CL", name: "Casablanca Valley", aliases: ["Casablanca"] },
  {
    code: "cl-central-valley",
    country: "CL",
    name: "Central Valley",
    aliases: ["Valle Central", "Curicó"],
  },
  { code: "uy-canelones", country: "UY", name: "Canelones" },
  // ---- the southern hemisphere ----
  { code: "za-stellenbosch", country: "ZA", name: "Stellenbosch" },
  { code: "za-swartland", country: "ZA", name: "Swartland" },
  { code: "za-franschhoek", country: "ZA", name: "Franschhoek" },
  { code: "za-paarl", country: "ZA", name: "Paarl" },
  { code: "za-walker-bay", country: "ZA", name: "Walker Bay", aliases: ["Hemel-en-Aarde"] },
  {
    code: "au-barossa",
    country: "AU",
    name: "Barossa Valley",
    aliases: ["Barossa", "Eden Valley"],
  },
  { code: "au-mclaren-vale", country: "AU", name: "McLaren Vale" },
  { code: "au-clare-valley", country: "AU", name: "Clare Valley" },
  { code: "au-coonawarra", country: "AU", name: "Coonawarra" },
  { code: "au-margaret-river", country: "AU", name: "Margaret River" },
  { code: "au-hunter-valley", country: "AU", name: "Hunter Valley" },
  { code: "au-yarra-valley", country: "AU", name: "Yarra Valley" },
  { code: "au-tasmania", country: "AU", name: "Tasmania" },
  { code: "nz-marlborough", country: "NZ", name: "Marlborough" },
  { code: "nz-central-otago", country: "NZ", name: "Central Otago" },
  { code: "nz-hawkes-bay", country: "NZ", name: "Hawke's Bay", aliases: ["Hawkes Bay"] },
  { code: "nz-martinborough", country: "NZ", name: "Martinborough", aliases: ["Wairarapa"] },
];

const BY_CODE: ReadonlyMap<string, Region> = new Map(REGIONS.map((r) => [r.code, r]));

/** Folded name to regions, a name can belong to more than one country ("Moselle"). */
const BY_NAME: ReadonlyMap<string, readonly Region[]> = (() => {
  const index = new Map<string, Region[]>();
  for (const region of REGIONS) {
    for (const name of [region.name, ...(region.aliases ?? [])]) {
      const key = foldName(name);
      index.set(key, [...(index.get(key) ?? []), region]);
    }
  }
  return index;
})();

export function regionByCode(code: string): Region | undefined {
  return BY_CODE.get(code);
}

export function isRegionCode(code: string): boolean {
  return BY_CODE.has(code);
}

/**
 * The code for a region (or a well-known appellation inside one) as someone typed it. When the
 * wine names its country the region has to be in it, so a "Bordeaux" typed on an Italian wine
 * counts for nothing rather than for France.
 */
export function regionCodeOf(text: string, country?: string): string | undefined {
  const found = BY_NAME.get(foldName(text));
  if (found === undefined) return undefined;
  const wanted = country?.toUpperCase();
  return (wanted === undefined ? found[0] : found.find((r) => r.country === wanted))?.code;
}
